// GPU フィルター実行器。レイヤーのテクスチャ (レイヤー空間) に対して passes を順に実行し、
// 結果のテクスチャ (呼び出し側が release) または RGBA を返す。

import { GLProgram } from '@/gl/program'
import { Quad } from '@/gl/quad'
import { createFramebuffer, deleteFramebuffer, type Framebuffer, type Texture } from '@/gl/texture'
import { RECT_VERT } from '@/render/shaders/common'
import { BLUR_DIR_FRAG, MOTION_FRAG, COMBINE_FRAG, POINT_FRAG, SURFACE_FRAG } from './shaders'
import type { FilterPass } from './registry'
import type { Rect } from '@/types/geometry'

export class FilterEngine {
  private programs: Record<FilterPass['shader'], GLProgram>
  private quad: Quad
  private pool: Framebuffer[] = []
  private size = { w: 0, h: 0 }

  constructor(private gl: WebGL2RenderingContext) {
    this.programs = {
      blurDir: new GLProgram(gl, RECT_VERT, BLUR_DIR_FRAG, 'f-blur'),
      motion: new GLProgram(gl, RECT_VERT, MOTION_FRAG, 'f-motion'),
      combine: new GLProgram(gl, RECT_VERT, COMBINE_FRAG, 'f-combine'),
      point: new GLProgram(gl, RECT_VERT, POINT_FRAG, 'f-point'),
      surface: new GLProgram(gl, RECT_VERT, SURFACE_FRAG, 'f-surface')
    }
    this.quad = new Quad(gl)
  }

  private acquire(w: number, h: number): Framebuffer {
    if (this.size.w !== w || this.size.h !== h) {
      for (const f of this.pool) deleteFramebuffer(this.gl, f)
      this.pool = []
      this.size = { w, h }
    }
    return this.pool.pop() ?? createFramebuffer(this.gl, w, h)
  }

  release(f: Framebuffer): void {
    if (f.texture.width === this.size.w && f.texture.height === this.size.h) this.pool.push(f)
    else deleteFramebuffer(this.gl, f)
  }

  /**
   * src (テクスチャ、サイズ w×h) にパスを適用する。戻り値は結果 FBO (release すること)。
   * center はビネットなどの中心 (テクスチャ座標)
   */
  run(src: Texture, w: number, h: number, passes: FilterPass[], center: { x: number; y: number }): Framebuffer {
    const gl = this.gl
    gl.disable(gl.BLEND)
    gl.disable(gl.SCISSOR_TEST)
    const toClip = new Float32Array([2 / w, 0, 0, 0, 2 / h, 0, -1, -1, 1])
    let cur: Texture = src
    let curFb: Framebuffer | null = null
    for (const pass of passes) {
      const out = this.acquire(w, h)
      const p = this.programs[pass.shader]
      gl.bindFramebuffer(gl.FRAMEBUFFER, out.fbo)
      gl.viewport(0, 0, w, h)
      p.use()
      gl.uniform4f(p.loc('uRect'), 0, 0, w, h)
      gl.uniformMatrix3fv(p.loc('uToClip'), false, toClip)
      gl.uniform2f(p.loc('uSize'), w, h)
      const p0 = pass.p0, p1 = pass.p1 ?? [0, 0, 0, 0]
      const p1x = Number.isNaN(p1[0]) ? center.x : p1[0]
      const p1y = Number.isNaN(p1[1]) ? center.y : p1[1]
      gl.uniform4f(p.loc('uP0'), p0[0], p0[1], p0[2], p0[3])
      gl.uniform4f(p.loc('uP1'), p1x, p1y, p1[2], p1[3])
      gl.activeTexture(gl.TEXTURE0)
      if (pass.shader === 'combine') {
        // uSrc = 元画像、uBlur = 直前の出力
        gl.bindTexture(gl.TEXTURE_2D, pass.useOriginalAsSrc ? src.tex : cur.tex)
        gl.uniform1i(p.loc('uSrc'), 0)
        gl.activeTexture(gl.TEXTURE1)
        gl.bindTexture(gl.TEXTURE_2D, cur.tex)
        gl.uniform1i(p.loc('uBlur'), 1)
      } else {
        gl.bindTexture(gl.TEXTURE_2D, cur.tex)
        gl.uniform1i(p.loc('uSrc'), 0)
      }
      this.quad.draw()
      gl.bindFramebuffer(gl.FRAMEBUFFER, null)
      if (curFb) this.release(curFb)
      curFb = out
      cur = out.texture
    }
    if (!curFb) {
      // パス無し: コピー
      curFb = this.acquire(w, h)
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null)
      const tmp = gl.createFramebuffer()!
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, tmp)
      gl.framebufferTexture2D(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, src.tex, 0)
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, curFb.fbo)
      gl.blitFramebuffer(0, 0, w, h, 0, 0, w, h, gl.COLOR_BUFFER_BIT, gl.NEAREST)
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null)
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null)
      gl.deleteFramebuffer(tmp)
    }
    return curFb
  }

  readPixels(f: Framebuffer, rect: Rect): Uint8ClampedArray {
    const gl = this.gl
    const out = new Uint8Array(rect.w * rect.h * 4)
    gl.bindFramebuffer(gl.FRAMEBUFFER, f.fbo)
    gl.pixelStorei(gl.PACK_ALIGNMENT, 1)
    gl.readPixels(rect.x, rect.y, rect.w, rect.h, gl.RGBA, gl.UNSIGNED_BYTE, out)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    return new Uint8ClampedArray(out.buffer)
  }

  dispose(): void {
    for (const p of Object.values(this.programs)) p.dispose()
    for (const f of this.pool) deleteFramebuffer(this.gl, f)
    this.pool = []
    this.quad.dispose()
  }
}
